import { useRef, useEffect, type CSSProperties } from "react";
import { cn } from "../lib/utils";

export type LogLevel = "info" | "warn" | "error" | "debug" | "success";

export interface LogEntry {
  id: string;
  level?: LogLevel;
  timestamp?: string;
  message: string;
  badge?: string;
}

interface LogViewerProps {
  entries: LogEntry[];
  maxHeight?: number | string;
  autoScroll?: boolean;
  showTimestamps?: boolean;
  showLevel?: boolean;
  fontMono?: boolean;
  className?: string;
  style?: CSSProperties;
  emptyText?: string;
}

const levelColors: Record<LogLevel, string> = {
  info:    "lv-info",
  warn:    "lv-warn",
  error:   "lv-error",
  debug:   "lv-debug",
  success: "lv-success",
};

export function LogViewer({
  entries,
  maxHeight = 320,
  autoScroll = true,
  showTimestamps = true,
  showLevel = true,
  fontMono = true,
  className,
  style,
  emptyText = "No log entries.",
}: LogViewerProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoScroll) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [entries, autoScroll]);

  return (
    <div
      className={cn("lv-root", fontMono && "lv-mono", className)}
      style={{ maxHeight: typeof maxHeight === "number" ? `${maxHeight}px` : maxHeight, ...style }}
      role="log"
      aria-live="polite"
      aria-label="Log output"
    >
      {entries.length === 0 ? (
        <span className="lv-empty">{emptyText}</span>
      ) : (
        entries.map(e => (
          <div key={e.id} className={cn("lv-row", e.level && levelColors[e.level])}>
            {showTimestamps && e.timestamp && (
              <span className="lv-ts">{e.timestamp}</span>
            )}
            {showLevel && e.level && (
              <span className={cn("lv-badge", `lv-badge-${e.level}`)}>{e.level.toUpperCase()}</span>
            )}
            {e.badge && <span className="lv-custom-badge">{e.badge}</span>}
            <span className="lv-msg">{e.message}</span>
          </div>
        ))
      )}
      <div ref={bottomRef} />
    </div>
  );
}
